<script>
import {useOrganizationStore} from "../../../stores/organizations";
import {useI18n} from "vue-i18n";
import {useAnalyticEventStore} from "../../../stores/analyticEvents";

export default {
  setup() {
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();


    const {tm, t} = useI18n();

    const toggleEdit = () => {
      if (organizationStore.editSettings) {
        organizationStore.saveSettings();
        analyticEventStore.logEvent({
          action: `settings-organization-edit-save`
        });
      } else {
        organizationStore.toggleEditSettings();
        analyticEventStore.setOrganizationId(organizationStore.organizationId);
        analyticEventStore.logEvent({
          action: `settings-organization-edit-start`
        });
      }
    };

    const cancelEditSettings = () => {
      organizationStore.cancelEditSettings();
      analyticEventStore.logEvent({
        action: `settings-organization-edit-cancel`
      });
    };

    const validateForm = () => {
      return organizationStore.validateForm();
    };

    return {
      t,
      tm,
      organizationStore,
      toggleEdit,
      cancelEditSettings,
      validateForm
    };
  },
  computed: {
    organization() {
      return this.organizationStore.organization;
    },
    editSettings() {
      return this.organizationStore.editSettings;
    },
    localOrganization(){
      return this.organizationStore.localOrganization;
    },
    validStates(){
      return this.tm('organization.valid_states');
    },
    timeZones(){
      return this.tm('organization.time_zones');
    },
    countries(){
      return this.tm('organization.valid_countries');
    },
    orgTypes(){
      return this.tm('organization.org_types');
    },
    canEdit() {
      return this.organizationStore.permissions.edit;
    },
    fiscalYearDisplay() {
      const d = this.organization?.fiscal_year_start_date;
      if (!d) return 'January 1 (default)';
      const date = new Date(d + 'T00:00:00');
      if (isNaN(date)) return 'January 1 (default)';
      return `${this.monthName(date.getMonth() + 1)} ${date.getDate()}`;
    }
  },
  watch:{
    "organizationStore.organizationId"(){
      this.organizationStore.getOrganization();
    }
  },
  mounted(){
    this.organizationStore.getOrganization();
  },
  methods: {
    // Display month name for fiscal year
    monthName(num) {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return months[(num || 1) - 1];
    }
  }
};
</script>

<template>
  <div class="w-full p-4 pt-2">
    <div class="bg-white rounded-xl shadow-lg p-6">
      <p class="pb-0 font-bold">
        Organization Settings
      </p>
    </div>
  </div>
  <div class="w-full p-4">
    <div class="bg-white rounded-xl shadow-lg p-6">
      <table class="w-full text-left">
        <!-- Full Name Section -->
        <tbody>
          <tr class="border-b">
            <td class="pt-2 pb-2">
              <div class="flex justify-start space-x-24">
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Name
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      class="absolute top-0"
                    >
                      {{ organization.name }}
                    </p>
                  </div>
                </div>
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Organization Type
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      class="absolute top-0"
                    >
                      {{ organization.org_type ? orgTypes[organization.org_type] : "No organization type set" }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="flex justify-start space-x-24 pt-5">
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Description
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      style="display: -webkit-box; -webkit-line-clamp: 3; overflow: hidden; text-overflow: ellipsis; -webkit-box-orient: vertical;"
                      class="description"
                    >
                      {{ organization.description || "No description" }}
                    </p>
                  </div>
                </div>
                <!--       FISCAL YEAR  -->
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Fiscal Year Start Date
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      v-if="!editSettings"
                      class="absolute top-0"
                    >
                      {{ fiscalYearDisplay }}
                    </p>
                    <input
                      v-else
                      v-model="localOrganization.fiscal_year_start_date"
                      type="date"
                      class="border rounded border-gray-300 px-2 py-1 absolute top-0"
                    >
                  </div>
                </div>
              </div>
            </td>
          </tr>

          <tr>
            <th class="pt-5 font-bold">
              Location
            </th>
          </tr>
          <tr class="border-b">
            <td class="pt-2 pb-2">
              <div class="flex justify-start space-x-24">
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Address
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      class="absolute top-0"
                    >
                      {{ organization.address || "No address set" }}
                    </p>
                  </div>
                </div>
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    City
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      class="absolute top-0"
                    >
                      {{ organization.city || "No city set" }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="flex justify-start space-x-24 pt-5">
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    State
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      class="absolute top-0"
                    >
                      {{ organization.state ? validStates[organization.state] : "No state set" }}
                    </p>
                  </div>
                </div>
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Zip Code
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      class="absolute top-0"
                    >
                      {{ organization.zipcode }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="flex justify-start space-x-24 pt-5">
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Country
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      class="absolute top-0"
                    >
                      {{ organization.country ? countries[organization.country] : "No country set" }}
                    </p>
                  </div>
                </div>
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Time Zone
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      class="absolute top-0"
                    >
                      {{ organization.timezone ? timeZones[organization.timezone] : "No time zone set" }}
                    </p>
                  </div>
                </div>
              </div>
            </td>
          </tr>

          <tr>
            <th class="pt-5 font-bold">
              Contact Information
            </th>
          </tr>
          <tr class="border-b">
            <td class="pt-2 pb-2">
              <div class="flex justify-start space-x-24">
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Email
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      class="absolute top-0"
                    >
                      {{ organization.email || "No email set" }}
                    </p>
                  </div>
                  <span
                    v-if="organizationStore.errors.email"
                    class="text-red-500 text-xs"
                  >{{ organizationStore.errors.email }}</span>
                </div>
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Phone Number
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      class="absolute top-0"
                    >
                      {{ organization.phone_number || "No phone number set" }}
                    </p>
                  </div>
                  <span
                    v-if="organizationStore.errors.phone_number"
                    class="text-red-500 text-xs"
                  >{{ organizationStore.errors.phone_number }}</span>
                </div>
              </div>
              <div class="flex justify-start space-x-24 pt-5">
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    Website URL
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      v-if="!editSettings"
                      class="absolute top-0"
                    >
                      {{ organization.website_url || "No website URL set" }}
                    </p>
                    <input
                      v-else
                      v-model="localOrganization.website_url"
                      type="text"
                      class="w-3/4 border rounded border-gray-300 px-2 py-1 absolute top-0"
                    >
                  </div>
                  <span
                    v-if="organizationStore.errors.website_url"
                    class="text-red-500 text-xs"
                  >{{ organizationStore.errors.website_url }}</span>
                </div>
              </div>
            </td>
          </tr>

          <!-- Social Links Section -->
          <tr>
            <th class="pt-5 font-bold">
              Social Links
            </th>
          </tr>
          <tr class="border-b">
            <td class="pt-2 pb-2">
              <div class="flex justify-start space-x-24">
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    <i class="fa-brands fa-facebook" />
                    Facebook
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      v-if="!editSettings"
                      class="absolute top-0"
                    >
                      {{ organization.facebook_url || "No Facebook URL set" }}
                    </p>
                    <input
                      v-else
                      v-model="localOrganization.facebook_url"
                      type="text"
                      class="w-3/4 border rounded border-gray-300 px-2 py-1 absolute top-0"
                    >
                  </div>
                  <span
                    v-if="organizationStore.errors.facebook_url"
                    class="text-red-500 text-xs"
                  >{{ organizationStore.errors.facebook_url }}</span>
                </div>
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    <i class="fa-brands fa-linkedin" />
                    LinkedIn
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      v-if="!editSettings"
                      class="absolute top-0"
                    >
                      {{ organization.linkedin_url || "No LinkedIn URL set" }}
                    </p>
                    <input
                      v-else
                      v-model="localOrganization.linkedin_url"
                      type="text"
                      class="w-3/4 border rounded border-gray-300 px-2 py-1 absolute top-0"
                    >
                  </div>
                  <span
                    v-if="organizationStore.errors.linkedin_url"
                    class="text-red-500 text-xs"
                  >{{ organizationStore.errors.linkedin_url }}</span>
                </div>
              </div>
              <div class="flex justify-start space-x-24">
                <div class="w-1/2">
                  <p
                    class="pb-2 font-bold text-xs text-gray-600"
                  >
                    <i class="fa-brands fa-youtube" />
                    YouTube
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      v-if="!editSettings"
                      class="absolute top-0"
                    >
                      {{ organization.youtube_url || "No YouTube URL set" }}
                    </p>
                    <input
                      v-else
                      v-model="localOrganization.youtube_url"
                      type="text"
                      class="w-3/4 border rounded border-gray-300 px-2 py-1 absolute top-0"
                    >
                  </div>
                  <span
                    v-if="organizationStore.errors.youtube_url"
                    class="text-red-500 text-xs"
                  >{{ organizationStore.errors.youtube_url }}</span>
                </div>
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    <i class="fa-brands fa-x-twitter" />
                    Twitter
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      v-if="!editSettings"
                      class="absolute top-0"
                    >
                      {{ organization.x_url || "No X URL set" }}
                    </p>
                    <input
                      v-else
                      v-model="localOrganization.x_url"
                      type="text"
                      class="w-3/4 border rounded border-gray-300 px-2 py-1 absolute top-0"
                    >
                  </div>
                  <span
                    v-if="organizationStore.errors.x_url"
                    class="text-red-500 text-xs"
                  >{{ organizationStore.errors.x_url }}</span>
                </div>
              </div>
              <div class="flex justify-start space-x-24">
                <div class="w-1/2">
                  <p class="pb-2 font-bold text-xs text-gray-600">
                    <i class="fa-brands fa-instagram" />
                    Instagram
                  </p>
                  <div
                    class="relative"
                    style="min-height: 40px;"
                  >
                    <p
                      v-if="!editSettings"
                      class="absolute top-0"
                    >
                      {{ organization.instagram_url || "No Instagram URL set" }}
                    </p>
                    <input
                      v-else
                      v-model="localOrganization.instagram_url"
                      type="text"
                      class="w-3/4 border rounded border-gray-300 px-2 py-1 absolute top-0"
                    >
                  </div>
                  <span
                    v-if="organizationStore.errors.instagram_url"
                    class="text-red-500 text-xs"
                  >{{ organizationStore.errors.instagram_url }}</span>
                </div>
              </div>
            </td>
          </tr>

          <!-- Buttons Section -->
          <tr>
            <th class="pt-4">
              <div class="flex space-x-2">
                <button
                  v-if="editSettings"
                  class="btn-outline"
                  @click="cancelEditSettings"
                >
                  Cancel
                </button>
                <button
                  v-if="canEdit"
                  :class="editSettings ? 'btn-filled' : 'btn-outline'"
                  @click="toggleEdit"
                >
                  {{ editSettings ? 'Save' : 'Edit' }}
                </button>
              </div>
            </th>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>


<style scoped lang="scss">
th {
  width: 25%;
  font-size: 10px;
  font-weight: normal;
  text-align: left;
  text-transform: uppercase;
  @apply text-slate-500;
  @apply px-2;
  //@apply py-2;
}

td {
  width: 25%;
  font-size: 12px;
  @apply px-2;
  //@apply py-4;
}

.relative {
  position: relative;
}

.absolute {
  position: absolute;
}

input {
  height: 30px;
}

p.absolute {
  line-height: 30px;
}

@media screen and (max-width: 720px) {
  .flex {
    flex-direction: row;
    align-items: center;
  }
}

.description {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>
